











































































































































































import {
  defineComponent, ref, computed, defineAsyncComponent,
} from '@vue/composition-api';
import type { PropType, SetupContext } from '@vue/composition-api';
import { Tabs, TabItem as Tab, Dialog } from '@hb/coffee';
import { ubtSetupPageButton, ubtSetupModuleExpose } from '@/shared/utils/ubt/ubt-setup';
import { RouteMarketEnum, FundListDataType, InterestRateTypeEnum } from '@/api/apply-product/types';
import { RepaymentModelToLabel, RepayMentTipsTitle } from '@/po/ProductResult';
import { getShowRateValue, RateTypeNames } from '../../until';
import type { RepayDialogDataType } from '../../types';
import RepayPlanList from '../RepayPlanList/index.vue';
import type { FeeItemConfig } from '../RepayPlanList/index.vue';
import InfoItem from '../InfoItem/index.vue';

const DialogView = defineAsyncComponent(() => Promise.resolve(Dialog.view));

export default defineComponent({
  components: {
    Tabs,
    Tab,
    DialogView,
    RepayPlanList,
    InfoItem,
  },
  props: {
    data: {
      type: Object as PropType<RepayDialogDataType>,
      default: () => ({}),
      required: true,
    },
    /** 多资方列表，目前仅众安小贷有此模式 */
    fundListData: {
      type: Array as PropType<FundListDataType[]>,
      default: () => [],
      required: false,
    },
    currentCheckFundIndex: {
      type: Number,
      default: 0,
      required: false,
    },
    repayOnSchedule: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  emits: ['repayment-change', 'fund-change'],
  setup(props: {
    data: RepayDialogDataType;
    fundListData: FundListDataType[];
    currentCheckFundIndex: number;
    repayOnSchedule: boolean;
  }, { emit }: SetupContext) {
    const currentIndex = ref(0);
    const isSettleFeeRuleExpand = ref(false);
    const bonusDialogShow = ref(false);
    const couponDialogShow = ref(false);

    /** 费用项配置 */
    const feeItemsConfig: FeeItemConfig[] = [
      { label: '本金', field: 'amount' },
      {
        label: (item) => (item.interestFees ? '息费' : '利息'),
        field: ['interestFees', 'interest'],
      },
      { label: '保费', field: 'insuranceFee' },
      { label: '费用', field: 'feeOnly' },
      { label: '担保费', field: 'chargeValue1' },
      { label: '担保咨询费', field: 'guaranteeConsultingFee' },
      { label: '- 优惠券抵扣', field: 'reductionAmount', styleClass: 'reductionAmount' },
    ];

    /** 还款总期数 */
    const totalPeriod = computed(() => props.data?.loanCalcList?.length || 0);

    /** 当前还款方式 */
    const currentRepayment = computed(() => {
      const { repaymentModel = [] } = props.data;
      return RepaymentModelToLabel[repaymentModel[currentIndex.value]] || '等额本息';
    });

    /** 是否显示结算费规则 */
    const needShowSettleFeeRules = computed(() => {
      const { settleFeeRule, routeMarket, settleFeeRate } = props.data;
      return settleFeeRule || (
        routeMarket !== RouteMarketEnum.SERIAL
        && !!settleFeeRate
      );
    });

    /** 前置信息条目 */
    const frontInfoItem = computed(() => {
      const { data, repayOnSchedule } = props;
      const {
        rateType = InterestRateTypeEnum.NONE,
        rateValue = 0,
        bonusRateValue,
        lender,
        calcMethod, couponDiscountRate, couponDiscountAmount,
      } = data;
      return [
        {
          label: `${RateTypeNames[rateType as keyof typeof RateTypeNames]}利率`,
          value: (rateType && rateValue) ? `${
            (repayOnSchedule && bonusRateValue)
              ? getShowRateValue(bonusRateValue)
              : getShowRateValue(rateValue)
          }%` : undefined,
          extraTips: {
            content: couponDiscountRate && couponDiscountRate > 0 ? `用券后综合年利率降至${getShowRateValue(couponDiscountRate, 2)}%` : '',
            modelTitle: '用券后利率',
            modelDesc: `用券后利率指：优惠券返现${couponDiscountAmount}元后，您本次借款所承担的实际成本对应的综合年化利率`,
            ubtId: 'finance_loan_apply_couponDiscountRate_repayPlanModel',
          },
        },
        {
          label: '计息方式',
          value: calcMethod,
        },
        {
          label: '资金方',
          value: lender,
        },
      ];
    });

    /** 剩余信息条目 */
    const rearInfoItem = computed(() => {
      const { data } = props;
      return [
        {
          label: '保险费率',
          value: data.insuranceRate ? `${data.insuranceRate}%` : undefined,
        },
        {
          label: '备注',
          value: data.notes,
        },
      ];
    });

    const showOrHideSettleFeeRule = () => {
      isSettleFeeRuleExpand.value = !isSettleFeeRuleExpand.value;
    };

    const handleCheckFund = (index: number) => {
      emit('fund-change', index);
    };

    const handleSelectRepaymentType = (index: number, item: string) => {
      currentIndex.value = index;
      emit('repayment-change', item);
    };

    const handleShowBonusConfirm = () => {
      bonusDialogShow.value = true;
      ubtSetupPageButton('finance_loan_apply_repayOntime_rule');
    };

    const handleShowCouponDesc = () => {
      couponDialogShow.value = true;
    };

    const showExtraTipsModel = (extraTips: any) => {
      Dialog.alert({
        title: extraTips.modelTitle || '',
        message: extraTips.modelDesc || '',
        closeable: false,
      });
      if (extraTips.ubtId) {
        ubtSetupPageButton(extraTips.ubtId);
      }
    };

    const renderExtraTips = (extraTips: any) => {
      if (extraTips.ubtId) {
        ubtSetupModuleExpose(extraTips.ubtId);
      }
      return extraTips.content;
    };


    return {
      currentIndex,
      isSettleFeeRuleExpand,
      bonusDialogShow,
      couponDialogShow,
      totalPeriod,
      currentRepayment,
      needShowSettleFeeRules,
      frontInfoItem,
      rearInfoItem,
      feeItemsConfig,
      showOrHideSettleFeeRule,
      handleCheckFund,
      handleSelectRepaymentType,
      handleShowBonusConfirm,
      handleShowCouponDesc,
      getShowRateValue,
      showExtraTipsModel,
      renderExtraTips,
      RepaymentModelToLabel,
      RepayMentTipsTitle,
    };
  },
});
